<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Badge from 'dashboard/components-next/badge/Badge.vue';
import TramiteTypeForm from './TramiteTypeForm.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { accountId } = useAccount();

const tramiteTypes = useMapGetter('tramiteTypes/getTramiteTypes');
const uiFlags = useMapGetter('tramiteTypes/getUIFlags');
const isLoading = computed(() => uiFlags.value.isFetching);

const search = ref('');
const showCreateModal = ref(false);

onMounted(() => {
  store.dispatch('tramiteTypes/fetchTramiteTypes').catch(() => {
    useAlert(t('TRAMITE_TYPES.FETCH_ERROR'));
  });
});

const filteredTypes = computed(() => {
  const term = search.value.trim().toLowerCase();
  if (!term) return tramiteTypes.value;
  return tramiteTypes.value.filter(type =>
    `${type.name} ${type.code}`.toLowerCase().includes(term)
  );
});

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
function formatDate(value) {
  if (!value) return '';
  return dateFormatter.format(new Date(value));
}

function goToEdit(id) {
  router.push({
    name: 'tramite_types_settings_edit',
    params: { accountId: accountId.value, typeId: String(id) },
  });
}

function handleCreated(type) {
  showCreateModal.value = false;
  if (type?.id) goToEdit(type.id);
}

async function deleteType(type) {
  if (
    // eslint-disable-next-line no-alert
    !window.confirm(t('TRAMITE_TYPES.DELETE_CONFIRM', { name: type.name }))
  ) {
    return;
  }
  try {
    await store.dispatch('tramiteTypes/deleteTramiteType', type.id);
    useAlert(t('TRAMITE_TYPES.DELETE_SUCCESS'));
  } catch (error) {
    const message =
      error.response?.data?.error || t('TRAMITE_TYPES.DELETE_ERROR');
    useAlert(message);
  }
}
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="t('TRAMITE_TYPES.LOADING')"
    :no-records-found="!tramiteTypes.length"
    :no-records-message="t('TRAMITE_TYPES.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('TRAMITE_TYPES.HEADER')"
        :description="t('TRAMITE_TYPES.DESCRIPTION')"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="t('TRAMITE_TYPES.NEW')"
            @click="showCreateModal = true"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="flex flex-col gap-5">
        <div class="relative max-w-sm">
          <span
            class="absolute -translate-y-1/2 i-lucide-search size-4 left-3 top-1/2 text-n-slate-10"
          />
          <input
            v-model="search"
            type="text"
            :placeholder="t('TRAMITE_TYPES.SEARCH')"
            class="py-2 pr-3 pl-9 w-full text-sm rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12"
          />
        </div>

        <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div
            v-for="type in filteredTypes"
            :key="type.id"
            class="flex flex-col p-4 rounded-xl border transition-shadow cursor-pointer group border-n-weak bg-n-solid-1 hover:shadow-md"
            @click="goToEdit(type.id)"
          >
            <div class="flex gap-2 justify-between items-start">
              <div class="flex gap-2.5 items-start min-w-0">
                <span
                  class="flex justify-center items-center rounded-lg size-9 bg-n-blue-3 text-n-blue-11 shrink-0"
                >
                  <span class="i-lucide-stamp size-5" />
                </span>
                <div class="min-w-0">
                  <h3
                    class="text-sm font-medium truncate text-n-slate-12 line-clamp-1"
                  >
                    {{ type.name }}
                  </h3>
                  <p class="font-mono text-xs text-n-slate-10">
                    {{ type.code }}
                  </p>
                </div>
              </div>
              <Button
                v-tooltip.top="t('TRAMITE_TYPES.DELETE')"
                icon="i-lucide-trash-2"
                size="xs"
                color="ruby"
                variant="ghost"
                @click.stop="deleteType(type)"
              />
            </div>

            <div class="flex flex-wrap gap-2 items-center mt-4">
              <Badge :color="type.active ? 'green' : 'gray'" size="small">
                {{
                  type.active
                    ? t('TRAMITE_TYPES.CARD.ACTIVE')
                    : t('TRAMITE_TYPES.CARD.INACTIVE')
                }}
              </Badge>
              <Badge color="gray" size="small">
                {{ type.stages_count || 0 }} {{ t('TRAMITE_TYPES.CARD.STAGES_COUNT') }}
              </Badge>
              <span class="text-xs text-n-slate-10">
                {{ t('TRAMITE_TYPES.CARD.UPDATED') }} {{ formatDate(type.updated_at) }}
              </span>
            </div>
          </div>
        </div>

        <p
          v-if="tramiteTypes.length && !filteredTypes.length"
          class="py-8 text-sm text-center text-n-slate-10"
        >
          {{ t('TRAMITE_TYPES.NO_RESULTS') }}
        </p>
      </div>
    </template>
  </SettingsLayout>

  <woot-modal
    v-model:show="showCreateModal"
    :on-close="() => (showCreateModal = false)"
  >
    <TramiteTypeForm
      @created="handleCreated"
      @cancel="showCreateModal = false"
    />
  </woot-modal>
</template>
